import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import {
  Alert,
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  SegmentedControl,
  Select,
  Skeleton,
  Table,
} from "stories/Admin";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import DailyBars from "./DailyBars";
import {
  AnalyticsFeature,
  AnalyticsUsage,
  CountBy,
  UsageDb,
  UsageStats,
} from "./usageTypes";
import styles from "./AppUsagePage.module.scss";

/*
 * Superadmin «Bruk av appen»: hva som faktisk brukes i appene. Tallene per funksjon kommer fra Firebase Analytics
 * (GA4) via GET /api/v1/superadmin/usage; Barn, Bønnelogg, Janazah og AiBønn har i tillegg tall fra iBønns egne
 * tabeller. Uten GA4-tilgang viser siden hvordan den kobles til.
 */

const nf = (n: number | undefined | null) =>
  n === undefined || n === null ? "–" : n.toLocaleString("nb-NO");

const platformLabel = (p: string) =>
  ({ android: "Android", ios: "iOS", web: "Web", ukjent: "Ukjent" })[
    p.toLowerCase()
  ] ?? p;

const resultLabel = (r: string) =>
  ({
    CREATED: "Publisert",
    DUPLICATE: "Fantes fra før",
    PENDING_REVIEW: "Til godkjenning",
    REJECTED_NOT_NOTICE: "Ikke en dødsannonse",
    REJECTED_BAD_IMAGE: "Dårlig bilde",
    RATE_LIMITED: "Sperret (for mange)",
    ERROR: "Feil",
  })[r] ?? r;

const dateLabel = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("nb-NO", {
    day: "numeric",
    month: "short",
  });

/** Endring i prosent mot forrige periode, som merke. */
const Trend: React.FC<{ now: number; before: number }> = ({ now, before }) => {
  if (!before) return now ? <Badge tone="mint">ny</Badge> : <span>–</span>;
  const pct = Math.round(((now - before) / before) * 100);
  const tone = pct > 5 ? "mint" : pct < -5 ? "rose" : "grey";
  return (
    <Badge tone={tone}>
      {pct > 0 ? "▲ +" : pct < 0 ? "▼ " : ""}
      {pct} %
    </Badge>
  );
};

const Stat: React.FC<{
  label: string;
  value: number | undefined | null;
  hint?: React.ReactNode;
  strong?: boolean;
}> = ({ label, value, hint, strong }) => (
  <div className={strong ? `${styles.stat} ${styles.strong}` : styles.stat}>
    <b>{nf(value)}</b>
    <span>{label}</span>
    {hint && <small>{hint}</small>}
  </div>
);

const Split: React.FC<{ items: CountBy[]; format?: (s: string) => string }> = ({
  items,
  format = platformLabel,
}) =>
  items.length ? (
    <div className={styles.split}>
      {items.map((i) => (
        <Badge key={i.label} tone="grey">
          {format(i.label)}: {nf(i.count)}
        </Badge>
      ))}
    </div>
  ) : null;

const PERIODS = [
  { value: 7, label: "7 dager" },
  { value: 30, label: "30 dager" },
  { value: 90, label: "90 dager" },
];

const isoDay = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Steg for å koble GA4 til, ut fra hva backend sier mangler. */
const SetupPanel: React.FC<{ ga: AnalyticsUsage }> = ({ ga }) => {
  const email = ga.serviceAccountEmail;
  const project = email?.split("@")[1]?.split(".")[0];
  return (
    <Card title="Slik kobler du til Firebase-statistikk">
      {ga.status === "ERROR" && (
        <Alert tone="error">
          {ga.errorKind === "API_DISABLED"
            ? "Google Analytics Data API er ikke slått på for prosjektet ennå."
            : ga.errorKind === "PERMISSION_DENIED"
              ? "Tjenestekontoen har ikke tilgang til GA4-egenskapen ennå."
              : "Google Analytics svarte med en feil."}
          {ga.message && <div className={styles.errorText}>{ga.message}</div>}
        </Alert>
      )}
      {ga.status === "NOT_CONFIGURED" && (
        <Alert tone="info">
          Firebase-statistikken er ikke koblet til ennå
          {ga.missing.length > 0 && ` (mangler ${ga.missing.join(", ")})`}.
        </Alert>
      )}
      <ol className={styles.steps}>
        <li>
          <b>Finn GA4-egenskapen.</b> Firebase-konsollen → Prosjektinnstillinger
          → Integrasjoner → Google Analytics. Egenskaps-ID-en er et tall
          {ga.propertyId ? (
            <>
              {" "}
              (backend bruker nå <code>{ga.propertyId}</code>)
            </>
          ) : null}
          .
        </li>
        <li>
          <b>Slå på «Google Analytics Data API»</b> i Google Cloud Console
          (API-er og tjenester → Bibliotek) for prosjektet
          {project ? (
            <>
              {" "}
              <code>{project}</code>
            </>
          ) : (
            " tjenestekontoen hører til"
          )}
          .
        </li>
        <li>
          <b>Gi tjenestekontoen lesetilgang.</b> GA4 → Admin →
          Tilgangsadministrasjon for egenskap → «+» → legg til{" "}
          {email ? <code>{email}</code> : "backendens tjenestekonto"} med rollen
          «Leser» (Viewer).
        </li>
        <li>
          <b>Sett miljøvariabler</b> på backend-appen i DigitalOcean hvis
          standardverdiene ikke passer: <code>GA4_PROPERTY_ID</code> (standard
          443971903). Tjenestekontoen er den samme som Firebase Admin bruker (
          <code>FIREBASE_SERVICE_ACCOUNT_BASE64</code>); en annen kan settes med{" "}
          <code>GA4_CREDENTIALS_BASE64</code> (Base64 av JSON-nøkkelen).
        </li>
        <li>
          <b>Registrer egendefinerte dimensjoner</b> (GA4 → Admin → Egendefinerte
          definisjoner → Opprett egendefinert dimensjon, omfang «Hendelse»):{" "}
          <code>screen</code>, <code>kind</code> og <code>status</code> trengs
          for å dele opp <code>feature_open</code> og «dagens»-hendelsene.
        </li>
      </ol>
      <p className={styles.muted}>
        Tallene i GA4 kan komme 24–48 timer forsinket; «akkurat nå» viser de
        siste 30 minuttene. Når tilgangen er på plass, trykk «Oppdater».
      </p>
    </Card>
  );
};

/** Tall fra iBønns egne tabeller. */
const DbSection: React.FC<{ db: UsageDb }> = ({ db }) => {
  const learnedColumns: Array<
    Column<NonNullable<UsageDb["barn"]>["topLearned"][number]>
  > = [
    {
      key: "title",
      header: "Lært mest",
      render: (i) => (
        <>
          {i.emoji ? `${i.emoji} ` : ""}
          {i.title}
        </>
      ),
    },
    {
      key: "count",
      header: "Barn",
      numeric: true,
      align: "right",
      render: (i) => nf(i.count),
    },
  ];
  return (
    <>
      <h2 className={styles.section}>Fra iBønns database</h2>
      <p className={styles.muted}>
        Disse tallene finnes allerede i backend og er uavhengige av perioden over.
      </p>
      <div className={styles.grid}>
        <Card title="Barn">
          {db.barn ? (
            <>
              <p className={styles.lead}>
                <b>{nf(db.barn.children)}</b> barn hos{" "}
                <b>{nf(db.barn.parents)}</b> foreldre
              </p>
              <div className={styles.stats}>
                <Stat label="Aktive siste 7 dager" value={db.barn.activeChildren7} />
                <Stat label="Aktive siste 30 dager" value={db.barn.activeChildren30} />
                <Stat label="Nye barn siste 30 dager" value={db.barn.newChildren30} />
                <Stat label="Punkter lært totalt" value={db.barn.learnedTotal} />
                <Stat label="Merker totalt" value={db.barn.badgesTotal} />
                <Stat label="Bønner markert" value={db.barn.prayerMarks30} hint="siste 30 dager" />
              </div>
              {db.barn.topLearned.length > 0 && (
                <Table
                  compact
                  className={styles.table}
                  columns={learnedColumns}
                  rows={db.barn.topLearned}
                  rowKey={(i) => i.id}
                />
              )}
            </>
          ) : (
            <p className={styles.muted}>Kunne ikke hente tallene.</p>
          )}
        </Card>

        <Card title="Bønnelogg">
          {db.bonnelogg ? (
            <>
              <div className={styles.stats}>
                <Stat label="Brukere siste 7 dager" value={db.bonnelogg.users7} />
                <Stat label="Brukere siste 30 dager" value={db.bonnelogg.users30} />
                <Stat label="Brukere totalt" value={db.bonnelogg.usersTotal} />
                <Stat label="Markeringer siste 7 dager" value={db.bonnelogg.entries7} />
                <Stat label="Markeringer siste 30 dager" value={db.bonnelogg.entries30} />
              </div>
              <h3 className={styles.sub}>Brukere som markerte bønner per dag</h3>
              <DailyBars
                unit="brukere"
                data={db.bonnelogg.series.map((p) => ({
                  date: p.date,
                  value: p.users,
                  detail: `${nf(p.count)} markeringer`,
                }))}
              />
              <p className={styles.muted}>
                Bare innloggede brukere synker bønneloggen til serveren.
              </p>
            </>
          ) : (
            <p className={styles.muted}>Kunne ikke hente tallene.</p>
          )}
        </Card>

        <Card title="Janazah">
          {db.janazah ? (
            <>
              <p className={styles.lead}>
                <b>{nf(db.janazah.submissionsTotal)}</b> plakater lastet opp fra
                appen ({nf(db.janazah.submissions30)} siste 30 dager)
              </p>
              <Split items={db.janazah.byResult} format={resultLabel} />
              <div className={styles.stats}>
                <Stat label="Dødsannonser totalt" value={db.janazah.noticesTotal} />
                <Stat label="Nye siste 30 dager" value={db.janazah.notices30} />
                <Stat label="Kommende janazah" value={db.janazah.upcoming} />
                <Stat label="Venter på godkjenning" value={db.janazah.pendingReview} />
                <Stat label="Har janazah-varsler på" value={db.janazah.pushFollowers} hint="sett siste 90 dager" />
              </div>
              <Split items={db.janazah.pushFollowersByPlatform} />
            </>
          ) : (
            <p className={styles.muted}>Kunne ikke hente tallene.</p>
          )}
        </Card>

        <Card title="AiBønn">
          {db.aibonn ? (
            <>
              <div className={styles.stats}>
                <Stat label="Spørsmål siste 7 dager" value={db.aibonn.questions7} />
                <Stat label="Spørsmål siste 30 dager" value={db.aibonn.questions30} />
                <Stat label="Spørsmål totalt" value={db.aibonn.questionsTotal} />
                <Stat label="Innloggede som spurte" value={db.aibonn.users30} hint="siste 30 dager" />
              </div>
              <h3 className={styles.sub}>Spørsmål per dag</h3>
              <DailyBars
                unit="spørsmål"
                data={db.aibonn.series.map((p) => ({ date: p.date, value: p.count }))}
              />
            </>
          ) : (
            <p className={styles.muted}>Kunne ikke hente tallene.</p>
          )}
        </Card>

        <Card title="Enheter som melder seg til backend">
          {db.devices ? (
            <>
              <div className={styles.stats}>
                <Stat label="Aktive installasjoner" value={db.devices.active30} hint="siste 30 dager" />
              </div>
              <Split items={db.devices.byPlatform} />
              <p className={styles.muted}>
                Installasjoner som har sendt innstillingene sine (valgt moské,
                janazah-varsler) de siste 30 dagene.
              </p>
            </>
          ) : (
            <p className={styles.muted}>Kunne ikke hente tallene.</p>
          )}
        </Card>
      </div>
    </>
  );
};

const AppUsagePage: React.FC = () => {
  const { execute } = useFetch<UsageStats>();
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [days, setDays] = useState(30);
  const [data, setData] = useState<UsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState<string>("");
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async (period: number, refresh = false) => {
    setIsLoading(true);
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - (period - 1));
    const query = `from=${isoDay(from)}&to=${isoDay(to)}${refresh ? "&refresh=true" : ""}`;
    const result = await executeRef.current(`/api/v1/superadmin/usage?${query}`, "GET");
    if (result) setData(result);
    setFailed(!result);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    load(days);
  }, [load, days]);

  const ga = data?.analytics;
  const features = useMemo(() => ga?.features ?? [], [ga]);
  const withData = features.filter((f) => f.events > 0);
  const chosen: AnalyticsFeature | undefined =
    features.find((f) => f.feature === selected) ?? withData[0];

  const featureColumns: Array<Column<AnalyticsFeature>> = [
    {
      key: "label",
      header: "Funksjon",
      render: (f) => (
        <span className={styles.featureName}>
          <Icon name={open === f.feature ? "chevronLeft" : "chevronRight"} size={14} />
          <b>{f.label}</b>
        </span>
      ),
    },
    {
      key: "users",
      header: "Brukere",
      numeric: true,
      align: "right",
      render: (f) => nf(f.users),
    },
    {
      key: "events",
      header: "Hendelser",
      numeric: true,
      align: "right",
      render: (f) => nf(f.events),
    },
    {
      key: "trend",
      header: "Mot forrige periode",
      align: "right",
      render: (f) => <Trend now={f.events} before={f.previousEvents} />,
    },
    {
      key: "platform",
      header: "Plattform (brukere)",
      render: (f) =>
        f.byPlatform.length
          ? f.byPlatform.map((p) => `${platformLabel(p.label)} ${nf(p.count)}`).join(" · ")
          : "–",
    },
  ];

  const a = ga?.active;
  const period = data ? `${dateLabel(data.from)}–${dateLabel(data.to)}` : "";

  return (
    <>
      <PageHeader
        title="Bruk av appen"
        subtitle="Hvilke funksjoner som faktisk brukes i iBønn-appene: Firebase Analytics, pluss tall fra iBønns egen database."
        actions={
          <div className={styles.headerActions}>
            <SegmentedControl
              label="Periode"
              options={PERIODS}
              value={days}
              onChange={(v) => setDays(v)}
            />
            <Button
              variant="secondary"
              icon={<Icon name="swap" size={16} />}
              onClick={() => load(days, true)}
              loading={isLoading}
            >
              Oppdater
            </Button>
          </div>
        }
      />
      <SectionTabs />

      {failed && !data ? (
        <Card>
          <EmptyState icon="alert" title="Kunne ikke hente tallene">
            Prøv igjen med «Oppdater».
          </EmptyState>
        </Card>
      ) : !data ? (
        <div className={styles.kpis}>
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={96} />
          ))}
        </div>
      ) : (
        <>
          {ga && ga.status !== "OK" ? (
            <SetupPanel ga={ga} />
          ) : (
            ga &&
            a && (
              <>
                <div className={styles.kpis}>
                  <Stat strong label="Aktive brukere i dag" value={a.day1} hint="siste døgn i GA4" />
                  <Stat strong label="Aktive siste 7 dager" value={a.day7} />
                  <Stat strong label="Aktive siste 28 dager" value={a.day28} />
                  <Stat
                    strong
                    label="Akkurat nå"
                    value={a.realtime}
                    hint={a.realtimeByPlatform.map((p) => `${platformLabel(p.label)} ${nf(p.count)}`).join(" · ") || "siste 30 min"}
                  />
                </div>

                <div className={styles.grid}>
                  <Card title="Aktive brukere per dag" subtitle={period}>
                    <DailyBars
                      unit="aktive brukere"
                      data={ga.series.map((p) => ({
                        date: p.date,
                        value: p.activeUsers,
                        detail: `${nf(p.sessions)} økter`,
                      }))}
                    />
                    <div className={styles.stats}>
                      <Stat label="Aktive i perioden" value={a.range} hint={<Trend now={a.range} before={a.previousRange} />} />
                      <Stat label="Økter" value={a.sessions} hint={<Trend now={a.sessions} before={a.previousSessions} />} />
                      <Stat label="Nye brukere" value={a.newUsers} />
                    </div>
                    <h3 className={styles.sub}>Plattform</h3>
                    <Split items={ga.byPlatform} />
                  </Card>

                  <Card title="Daglig bruk av én funksjon" subtitle={period}>
                    {withData.length ? (
                      <>
                        <Select
                          label="Funksjon"
                          value={chosen?.feature ?? ""}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelected(e.target.value)}
                        >
                          {withData.map((f) => (
                            <option key={f.feature} value={f.feature}>
                              {f.label}
                            </option>
                          ))}
                        </Select>
                        {chosen && (
                          <>
                            <DailyBars
                              unit="hendelser"
                              data={chosen.series.map((p) => ({
                                date: p.date,
                                value: p.events,
                                detail: `minst ${nf(p.users)} brukere`,
                              }))}
                            />
                            <div className={styles.stats}>
                              <Stat label="Brukere" value={chosen.users} hint={<Trend now={chosen.users} before={chosen.previousUsers} />} />
                              <Stat label="Hendelser" value={chosen.events} hint={<Trend now={chosen.events} before={chosen.previousEvents} />} />
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <p className={styles.muted}>Ingen funksjonshendelser i perioden.</p>
                    )}
                  </Card>
                </div>

                {ga.missingDimensions.length > 0 && (
                  <Alert tone="warn">
                    GA4 mangler de egendefinerte dimensjonene{" "}
                    {ga.missingDimensions.map((d, i) => (
                      <React.Fragment key={d}>
                        {i > 0 && ", "}
                        <code>{d}</code>
                      </React.Fragment>
                    ))}
                    . Opprett dem i GA4 → Admin → Egendefinerte definisjoner (omfang
                    «Hendelse»), så fordeles <code>feature_open</code> og
                    «dagens»-hendelsene fra de nye app-versjonene på riktig
                    funksjon. Til da vises de under «Andre hendelser».
                  </Alert>
                )}

                <Card
                  title="Funksjoner"
                  subtitle={`${period}. Trykk på en rad for å se hendelsene. «Brukere» er det største brukertallet blant funksjonens hendelser, så det reelle tallet er minst så høyt.`}
                >
                  <Table
                    columns={featureColumns}
                    rows={features}
                    rowKey={(f) => f.feature}
                    rowState={(f) => (f.events ? (open === f.feature ? "on" : undefined) : "dim")}
                    onRowClick={(f) => {
                      setOpen(open === f.feature ? null : f.feature);
                      if (f.events) setSelected(f.feature);
                    }}
                    renderExpanded={(f) =>
                      open === f.feature ? (
                        <ul className={styles.actions}>
                          {f.actions.map((x) => (
                            <li key={x.action}>
                              <span>
                                <b>{x.label}</b>
                                <small>
                                  {x.event ? <code>{x.event}</code> : "ingen ny hendelse"}
                                  {x.eventNames.filter((n) => n !== x.event).length > 0 &&
                                    ` · også ${x.eventNames.filter((n) => n !== x.event).join(", ")}`}
                                </small>
                              </span>
                              <span className={styles.actionNumbers}>
                                {nf(x.events)} hendelser · {nf(x.users)} brukere{" "}
                                <Trend now={x.events} before={x.previousEvents} />
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : null
                    }
                  />
                  <p className={styles.muted}>
                    Funksjoner uten tall er tonet ned: de nye app-versjonene begynner å
                    logge dem (Barn, Kahf, Tasbih-økter, Bønnelogg-markeringer …), så
                    tallene kommer når brukerne har oppdatert. Eldre hendelsesnavn
                    telles med, så historikken henger sammen.
                  </p>
                </Card>

                <div className={styles.grid}>
                  <Card title="Appversjoner" subtitle="Aktive brukere i perioden">
                    <ul className={styles.list}>
                      {ga.appVersions.slice(0, 12).map((v) => (
                        <li key={`${v.platform}-${v.version}`}>
                          <span>
                            {platformLabel(v.platform)} v{v.version}
                          </span>
                          <b>{nf(v.users)}</b>
                        </li>
                      ))}
                    </ul>
                  </Card>
                  <Card title="Andre hendelser" subtitle="Egne hendelser som ikke hører til noen funksjon ennå">
                    {ga.otherEvents.length ? (
                      <ul className={styles.list}>
                        {ga.otherEvents.map((e) => (
                          <li key={e.name}>
                            <code>{e.name}</code>
                            <b>
                              {nf(e.events)} <small>· {nf(e.users)} brukere</small>
                            </b>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className={styles.muted}>Ingen.</p>
                    )}
                  </Card>
                  <Card title="Skjermer" subtitle="screen_view etter skjermnavn">
                    <ul className={styles.list}>
                      {ga.screens.slice(0, 15).map((s) => (
                        <li key={s.name || "tom"}>
                          <span>{s.name || "(tomt navn)"}</span>
                          <b>
                            {nf(s.views)} <small>· {nf(s.users)} brukere</small>
                          </b>
                        </li>
                      ))}
                    </ul>
                    <p className={styles.muted}>
                      «(not set)» betyr at appen ikke navngir skjermen. De nye
                      app-versjonene logger skjermnavn (min_side, tasbih, barn …).
                    </p>
                  </Card>
                </div>
                <p className={styles.muted}>
                  Kilde: Firebase Analytics (GA4-egenskap {ga.propertyId}). Tallene
                  kan komme 24–48 timer forsinket; «akkurat nå» gjelder de siste 30
                  minuttene.
                </p>
              </>
            )
          )}

          <DbSection db={data.db} />

          <span className={styles.muted}>
            Oppdatert {new Date(data.generatedAt).toLocaleString("nb-NO")}.
          </span>
        </>
      )}
    </>
  );
};

export default AppUsagePage;
